// components/GoogleMap/AddressAutocomplete.jsx
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { loadGoogleMaps } from 'utils/googleMaps';

const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, className, disabled }) => {
  const containerRef = useRef(null);
  const elementRef = useRef(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isLoadingKey, setIsLoadingKey] = useState(true);

  const handlePlaceSelection = useCallback(async (place) => {
    try {
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents', 'id'],
      });

      const addressComponents = place.addressComponents || [];
      const result = {
        placeId: place.id,
        formattedAddress: place.formattedAddress,
        latitude: place.location?.lat(),
        longitude: place.location?.lng(),
        name: place.displayName,
        addressComponents: addressComponents.reduce((acc, component) => {
          const types = component.types;
          if (types.includes('street_number')) acc.streetNumber = component.longText;
          if (types.includes('route')) acc.streetName = component.longText;
          if (types.includes('locality')) acc.city = component.longText;
          if (types.includes('administrative_area_level_1')) acc.state = component.shortText;
          if (types.includes('postal_code')) acc.postalCode = component.longText;
          if (types.includes('country')) acc.country = component.shortText;
          return acc;
        }, {}),
      };

      onSelect?.(result);
    } catch (err) {
      console.error('Failed to fetch place fields:', err);
    }
  }, [onSelect]);

  useEffect(() => {
    let isMounted = true;
    let shadowInput = null;
    let handleInput = null;

    const init = async () => {
      try {
        setIsLoadingKey(true);
        // Use the shared loader to fetch key and load Google Maps
        const google = await loadGoogleMaps();
        setIsLoadingKey(false);
        
        if (!isMounted || !containerRef.current || elementRef.current) return;

        // Ensure the PlaceAutocompleteElement is available
        if (!google.maps.places.PlaceAutocompleteElement) {
          throw new Error('PlaceAutocompleteElement not loaded');
        }

        const el = new google.maps.places.PlaceAutocompleteElement({
          componentRestrictions: { country: 'us' },
        });
        el.style.width = '100%';
        if (placeholder) el.setAttribute('placeholder', placeholder);

        el.addEventListener('gmp-select', async (event) => {
          const place = event.placePrediction.toPlace();
          handlePlaceSelection(place);
        });

        containerRef.current.appendChild(el);
        elementRef.current = el;

        const attachShadowListener = () => {
          shadowInput = el.shadowRoot?.querySelector('input');
          if (shadowInput) {
            handleInput = (e) => onChange?.(e.target.value);
            shadowInput.addEventListener('input', handleInput);
            if (value) shadowInput.value = value;
          }
        };
        attachShadowListener();
        requestAnimationFrame(attachShadowListener);

        if (isMounted) setIsGoogleLoaded(true);
      } catch (err) {
        setIsLoadingKey(false);
        console.error('Failed to load Google Places element:', err);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (shadowInput && handleInput) {
        shadowInput.removeEventListener('input', handleInput);
      }
      if (elementRef.current && elementRef.current.parentNode) {
        elementRef.current.parentNode.removeChild(elementRef.current);
      }
      elementRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, handlePlaceSelection]);

  return (
    <div className="relative w-full">
      <div ref={containerRef} className={className} />
      {(!isGoogleLoaded || isLoadingKey) && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {disabled && (
        <div className="absolute inset-0 bg-white/50 cursor-not-allowed" />
      )}
    </div>
  );
};

export default AddressAutocomplete;